import { useCallback, useMemo, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  CalendarClock,
  Check,
  LayoutGrid,
  List,
  Loader2,
  MessageSquareText,
  RotateCcw,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useParametroDaUrl } from "@/lib/use-parametro-da-url";
import { useSetPageHeader } from "@/lib/page-header";
import { BotaoExportar } from "@/components/shared/BotaoExportar";
import { colunasServico } from "@/lib/colunas-export";
import { useAuth } from "@/lib/auth";
import { useOwnedOrganizations } from "@/lib/owned-organizations";
import { usePermissao } from "@/lib/use-permissao";
import { cn } from "@/lib/cn";
import { OwnedOrgsViewToggle } from "@/components/shared/OwnedOrgsViewToggle";
import { ProsthesisIncomingAggregatedView } from "@/components/prosthesis/ProsthesisIncomingAggregatedView";
import { ProductionOrderDetailDialog } from "@/components/production/ProductionOrderDetailDialog";
import { DeclineOrderDialog } from "@/components/production/DeclineOrderDialog";
import { STATUS_META, STATUS_TERMINAIS, formatBRL, formatDate } from "@/components/prosthesis/prosthesis-meta";
import { EditarServicoDialog } from "@/components/production/EditarServicoDialog";
import { AcoesDoPedido } from "@/components/production/AcoesDoPedido";
import { RequestReworkDialog, type ReworkTarget } from "@/components/prosthesis/RequestReworkDialog";
import { isUrgentDeadline } from "@/lib/prosthesis-urgency";
import {
  orderColorsSummary,
  orderIsPriced,
  orderMaterialsSummary,
  orderTotalCents,
  serviceLabel,
  type IncomingOrderRow,
} from "@/components/production/production-types";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { PEDIDOS_TOUR_STEPS } from "@/lib/product-tour/page-tour-steps";
import { IncomingOrdersMobileList } from "@/components/production/IncomingOrdersMobileList";
import { codigoDoPedido } from "@/lib/codigo-do-pedido";
import { situacaoDoPedido, usePrazoDoLaboratorio } from "@/lib/prazo-do-laboratorio";
import { SeloVenceEmBreve } from "@/components/production/PrazoComAtraso";
import type { PrazoDoLaboratorio } from "@bookdental/schemas";
import { useAceitarPedido } from "@/components/production/use-aceitar-pedido";

type TabKey = "pendentes" | "producao" | "concluidos" | "recusados";
type ViewMode = "card" | "list";

/**
 * Uma aba por etapa da vida do pedido, sem sobreposição — cada pedido está em exatamente um lugar.
 *
 * "Confirmados" saiu: ela queria dizer "aceito" mas na prática pegava tudo que não era pendente nem
 * recusado, incluindo o que já estava pronto e o que foi cancelado. Com Em produção e Concluídos ao
 * lado, o mesmo pedido apareceria em duas abas e a tela deixaria de responder onde cada um está.
 *
 * "Aceito" foi junto com "Em produção" porque é estado de passagem: o serviço vira Em produção
 * assim que entra em qualquer estágio, então uma aba só pra ele viveria quase sempre vazia.
 * "Cancelado" foi com "Recusados" porque, do ponto de vista de quem olha a lista, os dois respondem
 * a mesma coisa — não vai ser entregue.
 */
const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "pendentes", label: "Pendentes" },
  { key: "producao", label: "Em produção" },
  { key: "concluidos", label: "Concluídos" },
  { key: "recusados", label: "Recusados" },
];

/** Dentro de "Em produção": quanto do pedido já está pronto. */
type LenteProducao = "todos" | "parcial" | "nada";

const LENTES: Array<{ key: LenteProducao; label: string }> = [
  { key: "todos", label: "Todos" },
  { key: "parcial", label: "Parcial" },
  { key: "nada", label: "Nada pronto" },
];

/**
 * Quantos serviços do pedido já foram entregues.
 *
 * Cancelado NÃO conta: é terminal, mas não foi entregue — contá-lo mostraria como parcialmente
 * pronto um pedido do qual nada saiu.
 */
function prontosDoPedido(order: IncomingOrderRow): { prontos: number; total: number } {
  const servicos = order.services ?? [];
  return { prontos: servicos.filter((s) => s.status === "CONCLUIDO").length, total: servicos.length };
}

/**
 * Serviços do pedido em que cada ação faz sentido.
 *
 * A regra é do SERVIÇO, não da aba: um pedido "Em produção" pode ter serviço já entregue (é o que a
 * lente Parcial mostra), e aí lápis e refazer convivem na mesma linha. Amarrar o ícone à aba
 * esconderia a ação justamente no caso misto.
 */
function servicosEditaveis(order: IncomingOrderRow) {
  return (order.services ?? []).filter((s) => !STATUS_TERMINAIS.includes(s.status));
}

function servicosRefazeveis(order: IncomingOrderRow) {
  return (order.services ?? []).filter((s) => s.status === "CONCLUIDO");
}

function orderValueLabel(order: IncomingOrderRow): string {
  return orderIsPriced(order) ? formatBRL(orderTotalCents(order)) : "—";
}

/** Badge de contagem por aba — Pendentes fica em âmbar quando > 0 (ainda pede ação); as demais
 *  ficam neutras, é só um contador de histórico. */
function tabBadgeClasses(key: TabKey, active: boolean, count: number): string {
  if (active) return "bg-white/20 text-white";
  if (key === "pendentes" && count > 0) return "bg-amber-500 text-white";
  return "bg-zinc-200 text-zinc-600 dark:bg-muted dark:text-muted-foreground";
}

/** Borda lateral do card por aba — mesma cor da aba, pra escanear rápido em qual grupo o pedido
 *  está mesmo sem reler o badge de status. */
function tabCardBorderClasses(key: TabKey): string {
  switch (key) {
    case "pendentes":
      return "border-zinc-200 border-l-4 border-l-amber-500 dark:border-border dark:border-l-amber-400";
    case "recusados":
      return "border-zinc-200 border-l-4 border-l-red-500 dark:border-border dark:border-l-red-400";
    case "producao":
      return "border-zinc-200 border-l-4 border-l-brand-500 dark:border-border dark:border-l-primary";
    case "concluidos":
      return "border-zinc-200 border-l-4 border-l-emerald-500 dark:border-border dark:border-l-emerald-400";
  }
}

export function Pedidos() {
  const { user } = useAuth();
  const isPersonalOrg = !!user?.activeOrganization?.isPersonal;
  const { labs: ownedLabs } = useOwnedOrganizations();
  const otherOwnedLabs = useMemo(
    () => ownedLabs.filter((o) => o.id !== user?.activeOrganization?.id),
    [ownedLabs, user?.activeOrganization?.id],
  );
  const [view, setView] = useState<"own" | "aggregated">("own");
  const [tab, setTab] = useState<TabKey>("pendentes");
  const [lente, setLente] = useState<LenteProducao>("todos");
  const [viewMode, setViewMode] = useState<ViewMode>("card");
  // Guarda só o id — o objeto vem sempre fresco de `orders`, senão o dialog aberto continua
  // mostrando dado antigo depois de aceitar/recusar o próprio pedido nele.
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  useParametroDaUrl("abrir", setDetailOrderId);
  const [declineTargetId, setDeclineTargetId] = useState<string | null>(null);
  const [servicoAlvo, setServicoAlvo] = useState<any | null>(null);
  const [refazendo, setRefazendo] = useState<ReworkTarget>(null);
  const utils = trpc.useUtils();

  useTourDaPagina(PEDIDOS_TOUR_STEPS);
  useSetPageHeader(
    {
      title: "Pedidos",
      subtitle: "Todos os pedidos recebidos das clínicas conectadas",
    },
    [],
  );

  const listQuery = trpc.prosthesis.incoming.list.useQuery();
  // A planilha leva o nome dos pacientes das clínicas: permissão própria, não basta ver os pedidos.
  const podeExportar = usePermissao("production.export");
  const orders = useMemo(() => (listQuery.data ?? []) as IncomingOrderRow[], [listQuery.data]);
  const expectedDays = usePrazoDoLaboratorio();

  const pendentes = useMemo(() => orders.filter((o) => o.status === "AGUARDANDO_CONFIRMACAO"), [orders]);
  const producao = useMemo(
    () => orders.filter((o) => o.status === "ACEITO" || o.status === "EM_PRODUCAO"),
    [orders],
  );
  const concluidos = useMemo(() => orders.filter((o) => o.status === "CONCLUIDO"), [orders]);
  // Recusado é a negativa antes do aceite; cancelado é a desistência durante a produção. Motivos
  // diferentes, mesma resposta pra quem varre a lista: não vai ser entregue.
  const recusados = useMemo(
    () => orders.filter((o) => o.status === "RECUSADO" || o.status === "CANCELADO"),
    [orders],
  );
  const countByTab: Record<TabKey, IncomingOrderRow[]> = useMemo(
    () => ({ pendentes, producao, concluidos, recusados }),
    [pendentes, producao, concluidos, recusados],
  );

  const visibleOrders = useMemo(() => {
    const daAba = countByTab[tab];
    if (tab !== "producao" || lente === "todos") return daAba;
    return daAba.filter((o) => {
      const { prontos } = prontosDoPedido(o);
      return lente === "parcial" ? prontos > 0 : prontos === 0;
    });
  }, [tab, lente, countByTab]);
  // Uma linha por SERVIÇO dos pedidos visíveis — a aba e a lente já filtraram, então o arquivo sai
  // igual à tela. `prosthesis.incoming.list` não pagina, então basta desdobrar o que já está aqui.
  const linhasParaExportar = useCallback(
    async () =>
      visibleOrders.flatMap((pedido: any) =>
        (pedido.services ?? []).map((servico: any) => ({ pedido, servico })),
      ),
    [visibleOrders],
  );

  const detailOrder = useMemo(() => orders.find((o) => o.id === detailOrderId) ?? null, [orders, detailOrderId]);
  const declineTarget = useMemo(() => orders.find((o) => o.id === declineTargetId) ?? null, [orders, declineTargetId]);

  const accept = useAceitarPedido();

  const decline = trpc.prosthesis.incoming.declineOrder.useMutation({
    onSuccess: () => {
      toast.success("Pedido recusado.");
      utils.prosthesis.incoming.list.invalidate();
      setDeclineTargetId(null);
    },
    onError: (e) => toast.error(e.message || "Erro ao recusar pedido."),
  });

  const handleAccept = (order: IncomingOrderRow) => accept.aceitar(order);

  function handleDecline(order: IncomingOrderRow) {
    setDeclineTargetId(order.id);
  }

  const emptyMessage: Record<TabKey, { title: string; hint: string }> = {
    pendentes: {
      title: "Nenhum pedido pendente",
      hint: "Pedidos novos aparecem aqui aguardando sua confirmação.",
    },
    recusados: {
      title: "Nenhum pedido recusado ou cancelado",
      hint: "O que não vai ser entregue fica aqui como histórico — recusado antes do aceite ou cancelado durante a produção.",
    },
    producao: {
      title:
        lente === "parcial"
          ? "Nenhum pedido parcialmente pronto"
          : lente === "nada"
            ? "Todo pedido em produção já tem algum serviço pronto"
            : "Nenhum pedido em produção",
      hint:
        lente === "todos"
          ? "Pedidos aceitos aparecem aqui até o último serviço ficar pronto."
          : "Troque a segmentação pra ver os demais.",
    },
    concluidos: {
      title: "Nenhum pedido concluído ainda",
      hint: "Quando todos os serviços de um pedido ficarem prontos, ele passa pra cá e sai da Produção.",
    },
  };

  if (isPersonalOrg && view === "aggregated") {
    return (
      <div className="mx-auto w-full max-w-[1600px] space-y-4">
        <OwnedOrgsViewToggle otherCount={otherOwnedLabs.length} view={view} onChange={setView} />
        <ProsthesisIncomingAggregatedView />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1600px] space-y-4">
      {isPersonalOrg && otherOwnedLabs.length > 0 && (
        <OwnedOrgsViewToggle otherCount={otherOwnedLabs.length} view={view} onChange={setView} />
      )}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        {/* Sem rolagem: as três abas dividem a largura em partes iguais e a fonte se ajusta pelo
            espaço com `clamp()` — encolhe até 10.5px em 320px e volta a 13px quando há folga.
            Com só três itens dá pra caber sem esconder nada atrás de um gesto. */}
        <div
          data-tour="pedidos-tabs-status"
          className="flex w-full items-center gap-1 rounded-lg border border-zinc-200 bg-zinc-50 p-1 md:inline-flex md:w-auto dark:border-border dark:bg-muted/40"
        >
          {TABS.map((t) => {
            const active = tab === t.key;
            const count = countByTab[t.key].length;
            return (
              <button
                key={t.key}
                type="button"
                data-tour={t.key === "pendentes" ? "pedidos-pendentes" : undefined}
                onClick={() => setTab(t.key)}
                className={cn(
                  "inline-flex h-11 min-w-0 flex-1 items-center justify-center gap-1 rounded-md px-1.5 text-[clamp(10.5px,3vw,13px)] font-semibold transition-colors md:h-9 md:flex-none md:gap-2 md:px-4 md:text-[13px]",
                  active
                    ? "bg-brand-600 text-white shadow-sm"
                    : "text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
                )}
              >
                {t.label}
                <span
                  className={cn(
                    "inline-flex h-4 min-w-[16px] shrink-0 items-center justify-center rounded-full px-1 text-[10px] font-bold tabular-nums md:h-5 md:min-w-[20px] md:px-1.5 md:text-[11px]",
                    tabBadgeClasses(t.key, active, count),
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Exportar colado no alternador de visualização, num grupo só — solto no `justify-between`
            ele caía no meio da linha. Fica ali e não no topo porque age sobre a aba e a lente
            selecionadas, e é o que o arquivo vai refletir. */}
        <div className="flex w-full items-center gap-2 md:w-auto">
          {podeExportar && (
            <BotaoExportar
              nome={`pedidos-${tab}`}
              titulo="pedidos"
              tela="pedidos"
              planilha="pedidos"
              quantidade={visibleOrders.reduce((t: number, o: any) => t + (o.services?.length ?? 0), 0)}
              colunas={colunasServico((s) => (STATUS_META as Record<string, { label: string }>)[s]?.label ?? s)}
              idDe={(l) => l.servico.id}
              buscarLinhas={linhasParaExportar}
              disabled={visibleOrders.length === 0}
              className="h-12 shrink-0 justify-center bg-white md:h-9 dark:bg-card"
            />
          )}

          <div
            data-tour="pedidos-visualizacao"
            className="flex h-12 min-w-0 flex-1 items-center rounded-md border border-zinc-200 bg-white p-0.5 md:inline-flex md:h-9 md:flex-none dark:border-border dark:bg-card"
          >
            <button
              type="button"
              onClick={() => setViewMode("card")}
              aria-label="Visualizar em cards"
              className={cn(
                "inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded px-3 text-xs font-medium transition-colors md:h-8 md:flex-none",
                viewMode === "card"
                  ? "bg-brand-50 font-semibold text-brand-700 ring-1 ring-brand-200 dark:bg-primary/15 dark:text-primary dark:ring-primary/30"
                  : "text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
              )}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
              Cards
            </button>
            <button
              type="button"
              onClick={() => setViewMode("list")}
              aria-label="Visualizar em lista"
              className={cn(
                "inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded px-3 text-xs font-medium transition-colors md:h-8 md:flex-none",
                viewMode === "list"
                  ? "bg-brand-50 font-semibold text-brand-700 ring-1 ring-brand-200 dark:bg-primary/15 dark:text-primary dark:ring-primary/30"
                  : "text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
              )}
            >
              <List className="h-3.5 w-3.5" />
              Lista
            </button>
          </div>
        </div>
      </div>

      {/* A segmentação só existe onde ela responde alguma coisa. Nas outras abas todos os pedidos
          estão no mesmo ponto — nada a separar. */}
      {tab === "producao" && (
        <div className="mb-4 inline-flex items-center gap-1 rounded-lg border border-zinc-200 bg-white p-0.5 dark:border-border dark:bg-card">
          {LENTES.map((l) => {
            const quantos =
              l.key === "todos"
                ? producao.length
                : producao.filter((o) => {
                    const { prontos } = prontosDoPedido(o);
                    return l.key === "parcial" ? prontos > 0 : prontos === 0;
                  }).length;
            return (
              <button
                key={l.key}
                type="button"
                onClick={() => setLente(l.key)}
                aria-pressed={lente === l.key}
                className={cn(
                  "inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-[13px] font-medium transition-colors",
                  lente === l.key
                    ? "bg-brand-600 font-semibold text-white shadow-sm"
                    : "text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
                )}
              >
                {l.label}
                <span
                  className={cn(
                    "tabular-nums",
                    lente === l.key ? "text-white/80" : "text-zinc-400 dark:text-muted-foreground/70",
                  )}
                >
                  {quantos}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {listQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
        </div>
      ) : visibleOrders.length === 0 ? (
        <div className="rounded-lg border border-zinc-200 bg-white py-16 text-center dark:border-border dark:bg-card">
          <p className="text-sm font-medium text-zinc-900 dark:text-foreground">{emptyMessage[tab].title}</p>
          <p className="mt-1 text-xs text-zinc-500 dark:text-muted-foreground">{emptyMessage[tab].hint}</p>
        </div>
      ) : viewMode === "card" ? (
        <div className="flex flex-col gap-3">
          {visibleOrders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              tab={tab}
              isPending={accept.isPending || decline.isPending}
              isUrgent={isUrgentDeadline(order.deadline, order.createdAt, expectedDays)}
              venceEmBreve={situacaoDoPedido(order, expectedDays) === "venceEmBreve"}
              onAccept={() => handleAccept(order)}
              onDecline={() => handleDecline(order)}
              onViewDetail={() => setDetailOrderId(order.id)}
              onEditarServico={(servico) => setServicoAlvo(servico)}
            />
          ))}
        </div>
      ) : (
        <>
          {/* Celular tem lista própria; a tabela de oito colunas segue intacta de `md` pra cima. */}
          <div className="md:hidden">
            <IncomingOrdersMobileList
              orders={visibleOrders}
              onViewDetail={(order) => setDetailOrderId(order.id)}
            />
          </div>
          <div className="hidden md:block">
        <OrdersListView
          orders={visibleOrders}
          tab={tab}
          isPending={accept.isPending || decline.isPending}
          expectedDays={expectedDays}
          onAccept={handleAccept}
          onDecline={handleDecline}
          onViewDetail={(order) => setDetailOrderId(order.id)}
          onEditarServico={setServicoAlvo}
          onRefazerServico={(order, servico) =>
            setRefazendo({
              serviceId: servico.id,
              serviceLabel: serviceLabel(servico),
              patientName: order.patientName,
            })
          }
        />
          </div>
        </>
      )}

      <ProductionOrderDetailDialog order={detailOrder} onClose={() => setDetailOrderId(null)} />
      <EditarServicoDialog aberto={!!servicoAlvo} servico={servicoAlvo} onFechar={() => setServicoAlvo(null)} />
      <RequestReworkDialog target={refazendo} lado="laboratorio" onClose={() => setRefazendo(null)} />
      <DeclineOrderDialog
        order={declineTarget}
        isPending={decline.isPending}
        onClose={() => setDeclineTargetId(null)}
        onConfirm={(reason) => decline.mutate({ orderId: declineTarget!.id, reason })}
      />
    </div>
  );
}

function OrderCard({
  order,
  tab,
  isPending,
  isUrgent,
  venceEmBreve,
  onAccept,
  onDecline,
  onViewDetail,
  onEditarServico,
}: {
  order: IncomingOrderRow;
  tab: TabKey;
  isPending: boolean;
  isUrgent: boolean;
  venceEmBreve: boolean;
  onAccept: () => void;
  onDecline: () => void;
  onViewDetail: () => void;
  onEditarServico: (servico: any) => void;
}) {
  const statusMeta = STATUS_META[order.status];
  const podeGerenciar = usePermissao("production.manage");
  const { prontos, total } = prontosDoPedido(order);
  // Aceitar/recusar é escrita (`production.manage` no servidor) — sem a permissão o pedido
  // continua visível, mas sem os dois botões que só saberiam devolver 403.
  const isPendente = tab === "pendentes" && podeGerenciar;
  // Um serviço editável resolve direto; com vários, o pedido abre pra escolher qual.
  const editaveis = podeGerenciar ? servicosEditaveis(order) : [];
  const editar = editaveis.length
    ? () => (editaveis.length === 1 ? onEditarServico(editaveis[0]) : onViewDetail())
    : undefined;

  return (
    // O card inteiro abre o pedido — o ícone de olho era um alvo pequeno pra uma ação que é a
    // principal da linha. Botões internos (aceitar/recusar) param a propagação.
    <div
      role="button"
      tabIndex={0}
      onClick={onViewDetail}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onViewDetail();
        }
      }}
      aria-label={`Ver pedido de ${order.patientName}`}
      className={cn(
        "relative cursor-pointer rounded-xl border bg-white p-4 shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:bg-card",
        tabCardBorderClasses(tab),
      )}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1.1fr_1.6fr_1fr] sm:items-center">
        {/* Bloco esquerdo — Identificação / Status */}
        <div className="flex items-start gap-2 sm:border-r sm:border-zinc-100 sm:pr-4 dark:sm:border-border/60">
          {isPendente && <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />}
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-zinc-900 dark:text-foreground">
              <span className="mr-1.5 font-mono text-[11px] font-semibold text-zinc-400 dark:text-muted-foreground">#{codigoDoPedido(order)}</span>
              {order.patientName}
            </p>
            <p className="truncate text-xs text-zinc-500 dark:text-muted-foreground">
              {order.organization.isPersonal ? order.dentistName ?? "—" : order.organization.name}
            </p>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <span
                className="inline-block rounded-full px-2.5 py-1 text-[11px] font-bold uppercase"
                style={{ backgroundColor: statusMeta.bg, color: statusMeta.color }}
              >
                {statusMeta.label}
              </span>
              {isUrgent && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold uppercase text-amber-800 dark:bg-amber-500/15 dark:text-amber-400">
                  <AlertTriangle className="h-3 w-3" />
                  Urgente
                </span>
              )}
              {venceEmBreve && <SeloVenceEmBreve />}
              {/* O quanto já saiu, na própria linha. A segmentação diz QUAIS pedidos estão
                  parciais; este número diz QUANTO falta em cada um, sem trocar de aba. Só em
                  produção: antes do aceite não há o que contar, e depois é tudo ou nada. */}
              {tab === "producao" && total > 1 && (
                <span
                  className={cn(
                    "inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold uppercase",
                    prontos > 0
                      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-400"
                      : "bg-zinc-100 text-zinc-600 dark:bg-muted dark:text-muted-foreground",
                  )}
                  title={`${prontos} de ${total} serviços concluídos`}
                >
                  {prontos} de {total} prontos
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Bloco central — Especificações técnicas */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:border-r sm:border-zinc-100 sm:px-4 dark:sm:border-border/60">
          <Field label="Serviços" value={order.services.map((s) => serviceLabel(s)).join(", ") || "—"} />
          <Field label="Material" value={orderMaterialsSummary(order)} />
          <Field label="Cor" value={orderColorsSummary(order)} />
        </div>

        {/* Bloco direito — Prazos / Valores em destaque */}
        <div className="flex flex-col gap-2 sm:items-end sm:pl-4">
          <div className="flex w-full items-start gap-4 sm:justify-end">
            <Field label="Data do pedido" value={formatDate(order.createdAt)} align="right" className="w-[86px]" />
            <Field label="Prazo" value={formatDate(order.deadline)} align="right" className="w-[86px]" />
            <AcoesDoPedido order={order} onEditarServico={editar} className="-mr-1 -mt-1 max-sm:ml-auto" />
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
              Valor total
            </p>
            <p className="text-lg font-bold tabular-nums text-zinc-900 dark:text-foreground">
              {orderValueLabel(order)}
            </p>
          </div>

          {isPendente && (
            <div className="flex gap-2 max-md:w-full">
              <button
                type="button"
                disabled={isPending}
                onClick={(e) => {
                  e.stopPropagation();
                  onAccept();
                }}
                className="inline-flex h-11 flex-[1.3] items-center justify-center gap-1.5 rounded-md bg-emerald-600 px-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:opacity-60 md:h-9 md:flex-none"
              >
                <Check className="h-4 w-4" />
                Aceitar
              </button>
              <button
                type="button"
                disabled={isPending}
                onClick={(e) => {
                  e.stopPropagation();
                  onDecline();
                }}
                className="inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded-md border border-red-200 px-3 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60 md:h-9 md:flex-none dark:border-red-900/50 dark:text-red-400 dark:hover:bg-red-950/30"
              >
                <X className="h-4 w-4" />
                Recusar
              </button>
            </div>
          )}
        </div>
      </div>

      {order.description && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-300">
          <MessageSquareText className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{order.description}</p>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  align = "left",
  className,
}: {
  label: string;
  value: string;
  align?: "left" | "right";
  className?: string;
}) {
  return (
    <div className={cn(align === "right" ? "text-right" : "text-left", className)}>
      <p className="text-[10px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">{label}</p>
      <p className="text-sm font-medium text-zinc-900 dark:text-foreground">{value}</p>
    </div>
  );
}

function OrdersListView({
  orders,
  tab,
  isPending,
  expectedDays,
  onAccept,
  onDecline,
  onViewDetail,
  onEditarServico,
  onRefazerServico,
}: {
  orders: IncomingOrderRow[];
  tab: TabKey;
  isPending: boolean;
  expectedDays: PrazoDoLaboratorio;
  onAccept: (order: IncomingOrderRow) => void;
  onDecline: (order: IncomingOrderRow) => void;
  onViewDetail: (order: IncomingOrderRow) => void;
  onEditarServico: (servico: any) => void;
  onRefazerServico: (order: IncomingOrderRow, servico: any) => void;
}) {
  const podeGerenciar = usePermissao("production.manage");
  const showAcceptDecline = tab === "pendentes" && podeGerenciar;

  return (
    <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white dark:border-border dark:bg-card">
      <table className="w-full table-fixed text-sm">
        <colgroup>
          {/* Ações tem largura fixa (aceitar, recusar, refazer e o "⋯" cabem sempre); as demais dividem
              o resto. Antes a soma passava de 100% e empurrava as ações pra fora da tabela. */}
          <col className="w-[14%]" />
          <col className="w-[12%]" />
          <col className="w-[12%]" />
          <col className="w-[18%]" />
          <col className="w-[10%]" />
          <col className="w-[7%]" />
          <col className="w-[9%]" />
          <col className="w-[9%]" />
          <col className="w-[9%]" />
          <col className="w-[104px]" />
        </colgroup>
        <thead className="border-b border-zinc-100 bg-zinc-50/50 text-[11px] uppercase tracking-wider text-zinc-500 dark:border-border dark:bg-background/50 dark:text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left font-semibold">Paciente</th>
            <th className="px-2 py-2 text-left font-semibold">Clínica</th>
            <th className="px-2 py-2 text-center font-semibold">Status</th>
            <th className="px-2 py-2 text-left font-semibold">Serviços</th>
            <th className="px-2 py-2 text-left font-semibold">Material</th>
            <th className="px-2 py-2 text-left font-semibold">Cor</th>
            <th className="px-2 py-2 text-left font-semibold">Data</th>
            <th className="px-2 py-2 text-left font-semibold">Prazo</th>
            <th className="px-2 py-2 text-right font-semibold">Valor</th>
            <th className="px-2 py-2 text-center font-semibold">Ações</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => {
            const statusMeta = STATUS_META[order.status];
            const servicesText = order.services.map((s) => serviceLabel(s)).join(", ") || "—";
            const materialText = orderMaterialsSummary(order);
            const colorText = orderColorsSummary(order);
            const venceEmBreve = situacaoDoPedido(order, expectedDays) === "venceEmBreve";
            const clinicText = order.organization.isPersonal ? order.dentistName ?? "—" : order.organization.name;
            const editaveis = servicosEditaveis(order);
            const refazeveis = servicosRefazeveis(order);
            return (
              <tr
                key={order.id}
                onClick={() => onViewDetail(order)}
                title="Ver pedido completo"
                className="cursor-pointer border-b border-zinc-100 align-top transition-colors last:border-b-0 hover:bg-zinc-50 dark:border-border/50 dark:hover:bg-muted/40"
              >
                <td className="break-words px-3 py-2 font-medium text-zinc-900 dark:text-foreground">
                  {order.patientName}
                  {order.description && (
                    <MessageSquareText
                      className="ml-1 inline-block h-3.5 w-3.5 shrink-0 align-text-top text-amber-500"
                      aria-label="Pedido com observações"
                    />
                  )}
                </td>
                <td className="break-words px-2 py-2 text-zinc-700 dark:text-foreground/80">{clinicText}</td>
                <td className="px-2 py-2">
                  <span
                    className="block w-full rounded-full px-2 py-1 text-center text-[10px] font-bold uppercase leading-tight"
                    style={{ backgroundColor: statusMeta.bg, color: statusMeta.color }}
                  >
                    {statusMeta.label}
                  </span>
                </td>
                <td className="break-words px-2 py-2 text-zinc-700 dark:text-foreground/80">{servicesText}</td>
                <td className="break-words px-2 py-2 text-zinc-700 dark:text-foreground/80">{materialText}</td>
                <td className="break-words px-2 py-2 text-zinc-700 dark:text-foreground/80">{colorText}</td>
                <td className="whitespace-nowrap px-2 py-2 text-zinc-700 dark:text-foreground/80">
                  {formatDate(order.createdAt)}
                </td>
                <td className="whitespace-nowrap px-2 py-2 text-zinc-700 dark:text-foreground/80">
                  <span
                    className={cn(
                      "flex items-center gap-1",
                      venceEmBreve && "font-semibold text-amber-600 dark:text-amber-400",
                    )}
                    title={venceEmBreve ? "Vence em breve" : undefined}
                  >
                    {order.deadline || !venceEmBreve ? formatDate(order.deadline) : <CalendarClock className="h-3.5 w-3.5" aria-label="Vence em breve" />}
                    {isUrgentDeadline(order.deadline, order.createdAt, expectedDays) && (
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-label="Urgente" />
                    )}
                  </span>
                </td>
                <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums text-zinc-700 dark:text-foreground/80">
                  {orderValueLabel(order)}
                </td>
                <td className="px-2 py-1.5">
                  <div className="flex items-center justify-center gap-1">
                    {showAcceptDecline && (
                      <>
                      <button
                        type="button"
                        disabled={isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          onAccept(order);
                        }}
                        aria-label="Aceitar pedido"
                        title="Aceitar pedido"
                        className="rounded p-1 text-emerald-600 hover:bg-emerald-50 disabled:opacity-60 dark:hover:bg-emerald-950/30"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        disabled={isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDecline(order);
                        }}
                        aria-label="Recusar pedido"
                        title="Recusar pedido"
                        className="rounded p-1 text-red-600 hover:bg-red-50 disabled:opacity-60 dark:hover:bg-red-950/30"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      </>
                    )}

                    {podeGerenciar && refazeveis.length > 0 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (refazeveis.length === 1) onRefazerServico(order, refazeveis[0]);
                          else onViewDetail(order);
                        }}
                        aria-label={refazeveis.length === 1 ? "Refazer serviço" : "Refazer um dos serviços"}
                        title={
                          refazeveis.length === 1
                            ? `Refazer serviço: ${serviceLabel(refazeveis[0])}`
                            : `Refazer serviço (${refazeveis.length} concluídos — escolha no pedido)`
                        }
                        className="rounded p-1 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </button>
                    )}

                    {/* Editar vai no "⋯": com um serviço só, abre a edição; com vários, o pedido, onde cada
                        serviço tem o seu botão — escolher um arbitrário seria pior do que pedir a escolha. */}
                    <AcoesDoPedido
                      order={order}
                      onEditarServico={
                        podeGerenciar && editaveis.length > 0
                          ? () => (editaveis.length === 1 ? onEditarServico(editaveis[0]) : onViewDetail(order))
                          : undefined
                      }
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
